/**
 * useSingleTourGuide — Responsive Tour Guide Hook
 * React 16.8+ | Bootstrap 4/5 compatible | Mobile-first
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";

/* ─── CSS injected once ─────────────────────────────────────── */
const STYLES = `
  .tg-overlay-mask {
    position: fixed;
    inset: 0;
    z-index: 999999990;
    pointer-events: none;
  }

  .tg-popup {
    position: fixed;
    z-index: 999999999;
    width: min(340px, calc(100vw - 24px));
    background: #ffffff;
    border-radius: 12px;
    box-shadow:
      0 4px 6px -1px rgba(2,6,23,.08),
      0 12px 32px -4px rgba(2,6,23,.18);
    pointer-events: auto;
    animation: tg-pop .22s cubic-bezier(.34,1.4,.64,1) both;
    transform-origin: top left;
  }

  /* Mobile: bottom-sheet style */
  @media (max-width: 575.98px) {
    .tg-popup {
      position: fixed !important;
      bottom: 16px !important;
      left: 12px !important;
      right: 12px !important;
      top: auto !important;
      width: auto !important;
      max-width: 100%;
      border-radius: 16px;
      animation: tg-slide-up .28s cubic-bezier(.34,1.3,.64,1) both;
      transform-origin: bottom center;
    }
  }

  /* Tablet */
  @media (min-width: 576px) and (max-width: 991.98px) {
    .tg-popup {
      width: min(360px, calc(100vw - 32px));
    }
  }

  @keyframes tg-pop {
    from { opacity: 0; transform: scale(.88) translateY(-6px); }
    to   { opacity: 1; transform: scale(1)  translateY(0); }
  }

  @keyframes tg-slide-up {
    from { opacity: 0; transform: translateY(20px) scale(.96); }
    to   { opacity: 1; transform: translateY(0)    scale(1); }
  }

  .tg-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    padding: 14px 16px 0;
  }

  .tg-title {
    margin: 0;
    font-size: clamp(13px, 1.6vw, 15px);
    font-weight: 700;
    color: #111827;
    line-height: 1.35;
    letter-spacing: -.01em;
  }

  .tg-close {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: #f3f4f6;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    color: #6b7280;
    font-size: 13px;
    transition: background .15s, color .15s;
    margin-top: -2px;
  }
  .tg-close:hover { background: #e5e7eb; color: #111827; }
  .tg-close:focus { outline: 2px solid #6366f1; outline-offset: 2px; }

  .tg-body {
    padding: 10px 16px 14px;
    font-size: clamp(12px, 1.5vw, 13.5px);
    color: #4b5563;
    line-height: 1.6;
  }

  .tg-badge {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #6366f1;
    background: #eef2ff;
    border-radius: 999px;
    padding: 2px 9px;
    margin-bottom: 6px;
  }

  .tg-arrow {
    position: fixed;
    width: 0;
    height: 0;
    z-index: 999999998;
    pointer-events: none;
  }
  .tg-arrow.tg-arrow--down {
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 9px solid #ffffff;
    filter: drop-shadow(0 -2px 3px rgba(2,6,23,.08));
  }
  .tg-arrow.tg-arrow--up {
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 9px solid #ffffff;
    filter: drop-shadow(0 2px 3px rgba(2,6,23,.08));
  }

  .tg-ring {
    position: fixed;
    pointer-events: none;
    border-radius: 10px;
    z-index: 999999991;
    animation: tg-ring-pulse 1.4s ease-in-out infinite;
  }
  @keyframes tg-ring-pulse {
    0%,100% { box-shadow: 0 0 0 3px rgba(99,102,241,.7); }
    50%      { box-shadow: 0 0 0 7px rgba(99,102,241,.2); }
  }

  .tg-footer {
    border-top: 1px solid #f3f4f6;
    padding: 10px 16px;
    display: flex;
    justify-content: flex-end;
  }
  .tg-btn-dismiss {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    transition: background .15s, color .15s;
  }
  .tg-btn-dismiss:hover { background: #f3f4f6; color: #374151; }
`;

let stylesInjected = false;
function injectStyles() {
    if (stylesInjected || typeof document === "undefined") return;
    const el = document.createElement("style");
    el.dataset.tourGuide = "1";
    el.textContent = STYLES;
    document.head.appendChild(el);
    stylesInjected = true;
}

/* ─── Positioning helpers ───────────────────────────────────── */
const POPUP_WIDTH  = 340;
const POPUP_HEIGHT = 140;
const ARROW_H      = 9;
const MARGIN       = 8;

function calcPosition(rect, viewW, viewH) {
    const spaceBelow = viewH - rect.bottom;
    const spaceAbove = rect.top;

    const placeBelowIdeal = rect.bottom + ARROW_H + MARGIN;
    const placeAboveIdeal = rect.top - ARROW_H - MARGIN - POPUP_HEIGHT;

    let placement;
    if (spaceBelow >= POPUP_HEIGHT + ARROW_H + MARGIN * 2) {
        placement = "below";
    } else if (spaceAbove >= POPUP_HEIGHT + ARROW_H + MARGIN * 2) {
        placement = "above";
    } else {
        placement = spaceBelow >= spaceAbove ? "below" : "above";
    }

    const rawLeft    = rect.left;
    const clampedLeft = Math.max(MARGIN, Math.min(rawLeft, viewW - POPUP_WIDTH - MARGIN));

    const arrowCenterOnTarget = rect.left + rect.width / 2;
    const arrowLeft = Math.max(12, Math.min(arrowCenterOnTarget - clampedLeft - 8, POPUP_WIDTH - 28));

    if (placement === "below") {
        return {
            popupTop:     placeBelowIdeal,
            popupLeft:    clampedLeft,
            arrowTop:     placeBelowIdeal - ARROW_H,
            arrowLeft:    clampedLeft + arrowLeft,
            arrowVariant: "down",
        };
    } else {
        return {
            popupTop:     placeAboveIdeal,
            popupLeft:    clampedLeft,
            arrowTop:     placeAboveIdeal + POPUP_HEIGHT + MARGIN,
            arrowLeft:    clampedLeft + arrowLeft,
            arrowVariant: "up",
        };
    }
}

/* ─── Hook ──────────────────────────────────────────────────── */
export function useSingleTourGuide() {
    injectStyles();

    const [state, setState] = useState({
        visible: false,
        rect:    null,
        title:   "",
        content: "",
        badge:   "",
        key:     null,
    });

    const [disabledKeys, setDisabledKeys] = useState([]);
    const popupRef = useRef(null);

    useEffect(() => {
        try {
            const stored = JSON.parse(
                localStorage.getItem("tour_guide_disabled_keys") || "[]"
            );
            setDisabledKeys(Array.isArray(stored) ? stored : []);
        } catch {
            setDisabledKeys([]);
        }
    }, []);

    const showGuide = useCallback((target, config) => {
        if (!target || !config?.key) return;
        if (disabledKeys.includes(config.key)) return;
        setTimeout(() => {
            const rect = target.getBoundingClientRect();
            setState({
                visible: true,
                rect,
                title:   config.title   || "",
                content: config.content || "",
                badge:   config.badge   || "",
                key:     config.key,
            });
        }, 50);
    }, [disabledKeys]);

    const closeGuide = useCallback(() => {
        if (!state.key) return;
        setState((s) => ({ ...s, visible: false }));
        setDisabledKeys((prev) => {
            const updated = Array.from(new Set([...prev, state.key]));
            try {
                localStorage.setItem("tour_guide_disabled_keys", JSON.stringify(updated));
            } catch (e) {
                // storage unavailable (private mode / blocked) — guide just won't persist
            }
            return updated;
        });
    }, [state.key]);

    useEffect(() => {
        if (!state.visible) return;
        const onKey = (e) => { if (e.key === "Escape") closeGuide(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [state.visible, closeGuide]);

    function Overlay() {
        if (!state.visible || !state.rect) return null;

        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const isMobile = vw < 576;
        const { rect } = state;
        const pos = isMobile ? null : calcPosition(rect, vw, vh);

        return createPortal(
            <>
                {/* SVG Mask */}
                <div className="tg-overlay-mask" aria-hidden="true">
                    <svg width="100%" height="100%">
                        <defs>
                            <mask id="tg-mask">
                                <rect width="100%" height="100%" fill="white" />
                                <rect
                                    x={rect.left - 4}   y={rect.top - 4}
                                    width={rect.width + 8} height={rect.height + 8}
                                    rx={10} ry={10}
                                    fill="black"
                                />
                            </mask>
                        </defs>
                        <rect
                            width="100%" height="100%"
                            fill="rgba(15,20,40,0.55)"
                            mask="url(#tg-mask)"
                        />
                    </svg>
                </div>

                {/* Highlight ring */}
                <div
                    className="tg-ring"
                    aria-hidden="true"
                    style={{
                        top:    rect.top    - 4,
                        left:   rect.left   - 4,
                        width:  rect.width  + 8,
                        height: rect.height + 8,
                    }}
                />

                {/* Arrow — desktop only */}
                {!isMobile && pos && (
                    <div
                        className={`tg-arrow tg-arrow--${pos.arrowVariant}`}
                        aria-hidden="true"
                        style={{ top: pos.arrowTop, left: pos.arrowLeft }}
                    />
                )}

                {/* Popup */}
                <div
                    ref={popupRef}
                    className="tg-popup"
                    role="dialog"
                    aria-modal="false"
                    aria-labelledby="tg-title"
                    style={!isMobile && pos ? { top: pos.popupTop, left: pos.popupLeft } : undefined}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="tg-header">
                        <div style={{ flex: 1 }}>
                            {state.badge && <span className="tg-badge">{state.badge}</span>}
                            <h4 className="tg-title" id="tg-title">{state.title}</h4>
                        </div>
                        <button className="tg-close" onClick={closeGuide} aria-label="Tutup panduan">
                            ✕
                        </button>
                    </div>

                    <div className="tg-body">{state.content}</div>
                </div>
            </>,
            document.body
        );
    }

    return { showGuide, closeGuide, Overlay, isGuideActive: state.visible, disabledKeys };
}